/**
 * A very small typed-by-convention event bus. Layers never import one another;
 * they speak through this, which is what keeps the registry swappable.
 *
 * Known signals:
 *   'gaze'       {object, kind, point, distance}        — attention landed
 *   'gaze:lost'  {}                                     — attention left
 *   'remember'   {kind, position, object, hue}          — a desire turned
 *   'telescope:enter' {telescope} / 'telescope:exit' {} — eye to the eyepiece
 *   'journey'    {t, velocity}                          — position on the stair
 *   'arrive'     {}                                     — the foot of the stair
 *   'audio:ready'{}                                     — the context is open
 */
export class Bus {
  constructor() {
    this._map = new Map();
  }

  on(type, fn) {
    if (!this._map.has(type)) this._map.set(type, new Set());
    this._map.get(type).add(fn);
    return () => this.off(type, fn);
  }

  once(type, fn) {
    const off = this.on(type, (payload) => {
      off();
      fn(payload);
    });
    return off;
  }

  off(type, fn) {
    this._map.get(type)?.delete(fn);
  }

  emit(type, payload) {
    const set = this._map.get(type);
    if (!set) return;
    for (const fn of set) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[bus] listener for "${type}" threw`, err);
      }
    }
  }

  get types() {
    return [...this._map.keys()];
  }
}
